import React, { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { styled } from '@compiled/react';
import Button, { type ButtonProps } from '@atlaskit/button';
import { Box, Flex, xcss, Text, Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useDebouncedCallback } from '@atlassian/jira-platform-use-debounce';
import {
	type UIAnalyticsEvent,
	useAnalyticsEvents,
	fireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';

import { DEBOUNCE_DELAY } from '../../../../common/constants';
import type { SupportedThirdParty } from '../../../../common/constants/import-type';
import type { ThirdPartyFieldConfigStateForItem } from '../../../../common/types';
import { AsyncMappingTable } from '../../../../common/ui/mapping-table';
import { SearchBar } from '../../../../common/ui/search-bar';
import { useContentForThirdParty } from '../../../../common/utils';
import { useThirdPartyFieldConfigState } from '../../../../controllers/import-state';
import messages from './messages';
import { getItemTypeContent, useTableHead, useTableRows } from './utils';

type FieldsMappingContentProps = {
	thirdParty: SupportedThirdParty;
	thirdPartyFieldConfigState: ThirdPartyFieldConfigStateForItem;
	rowsPerPage?: number;
	itemTypes?: string[];
	selectedItemType: string | null;
	onChange: (itemType: string) => void;
};

export const FieldsMappingContent = ({
	thirdParty,
	thirdPartyFieldConfigState,
	rowsPerPage = 7,
	itemTypes,
	selectedItemType,
	onChange,
}: FieldsMappingContentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [searchTerm, setSearchTerm] = useState<string>('');
	const { formatMessage } = useIntl();

	const [handleSearchTermChange] = useDebouncedCallback((newValue: string) => {
		if (newValue !== '') {
			fireTrackAnalytics(createAnalyticsEvent({}), 'fieldSearch used');
		}
	}, DEBOUNCE_DELAY);

	const onSearchTermChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setSearchTerm(e.target.value);
			handleSearchTermChange(e.target.value);
		},
		[handleSearchTermChange],
	);

	const { thirdPartyFieldMappings, thirdPartyFieldOrder } = thirdPartyFieldConfigState;
	const { fieldsMappingPageIssueTypesMessage } = useContentForThirdParty(thirdParty);

	const filteredFields = useMemo(
		() =>
			thirdPartyFieldOrder.filter(
				(fieldId) =>
					searchTerm === '' ||
					thirdPartyFieldMappings[fieldId].thirdPartyField.fieldName
						?.toLowerCase()
						.includes(searchTerm.toLowerCase()),
			),
		[searchTerm, thirdPartyFieldMappings, thirdPartyFieldOrder],
	);

	const tableHead = useTableHead(thirdParty);
	const tableRows = useTableRows(thirdParty, {
		...thirdPartyFieldConfigState,
		thirdPartyFieldOrder: filteredFields,
	});

	const onSetPage = useCallback(
		(pageNumber: number, uiAnalytics?: UIAnalyticsEvent) => {
			if (uiAnalytics) {
				fireUIAnalytics(uiAnalytics, 'fieldMappingTable', {
					pageNumber,
					totalPages: tableRows?.length ? Math.ceil(tableRows.length / rowsPerPage) : 0,
				});
			}
		},
		[rowsPerPage, tableRows?.length],
	);

	const headerContent = () => {
		if (!itemTypes || itemTypes.length < 2) {
			return (
				<SearchWrapper>
					<SearchBar
						onSearchTermChange={onSearchTermChange}
						placeholder={messages.searchFieldPlaceholder}
					/>
				</SearchWrapper>
			);
		}

		return (
			<Inline
				space="space.100"
				xcss={tabHeaderStyles}
				grow="fill"
				spread="space-between"
				shouldWrap
				rowSpace="space.150"
			>
				<Flex alignItems="center" gap="space.100" xcss={tabContentStyles}>
					{fieldsMappingPageIssueTypesMessage && (
						<Box xcss={textStyles}>
							<Text align="center">{fieldsMappingPageIssueTypesMessage}</Text>
						</Box>
					)}
					<Flex gap="space.100" xcss={tabsStyles}>
						{itemTypes.map((itemType) => (
							<StyledButton
								key={itemType}
								appearance="default"
								onClick={() => onChange(itemType)}
								isSelected={selectedItemType === itemType}
							>
								{getItemTypeContent(itemType, formatMessage)}
							</StyledButton>
						))}
					</Flex>
				</Flex>
				<Box xcss={searchBarStyles}>
					<SearchBar
						onSearchTermChange={onSearchTermChange}
						placeholder={messages.searchFieldPlaceholder}
						isCompact
					/>
				</Box>
			</Inline>
		);
	};

	return (
		<>
			{headerContent()}
			<Box paddingBlockStart="space.100">
				<AsyncMappingTable
					head={tableHead}
					rows={tableRows}
					rowsPerPage={rowsPerPage}
					emptyMessage={messages.noFields}
					onSetPage={onSetPage}
				/>
			</Box>
		</>
	);
};

type FieldsMappingContentWithTabsProps = {
	thirdParty: SupportedThirdParty;
	rowsPerPage?: number;
	onItemTypeChanged: (itemType: string) => void;
	itemTypes: string[];
	selectedItemType: string | null;
};

export const FieldsMappingContentWithTabs = ({
	thirdParty,
	rowsPerPage = 7,
	onItemTypeChanged,
	itemTypes,
	selectedItemType,
}: FieldsMappingContentWithTabsProps) => {
	const [thirdPartyFieldConfigState] = useThirdPartyFieldConfigState();

	if (thirdPartyFieldConfigState == null) {
		return null;
	}

	return (
		<FieldsMappingContent
			itemTypes={itemTypes}
			thirdParty={thirdParty}
			thirdPartyFieldConfigState={thirdPartyFieldConfigState}
			rowsPerPage={rowsPerPage}
			selectedItemType={selectedItemType}
			onChange={onItemTypeChanged}
		/>
	);
};

const tabHeaderStyles = xcss({
	marginTop: 'space.150',
	marginBottom: 'space.100',
});

const textStyles = xcss({
	minWidth: '70px',
	fontSize: '12px',
	fontWeight: 500,
	paddingTop: 'space.050',
	paddingBottom: 'space.075',
});

const tabContentStyles = xcss({
	maxWidth: '100%',
	minWidth: '75%',
	flexGrow: 1,
});

const searchBarStyles = xcss({
	width: '140px',
	flex: 1,
	maxWidth: '240px',
	minWidth: '140px',
	paddingTop: 'space.050',
});

const tabsStyles = xcss({
	overflow: 'auto',
	padding: 'space.050',
	paddingBottom: 'space.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)<ButtonProps>({
	'&&': {
		borderRadius: '20px',
		textTransform: 'capitalize',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchWrapper = styled.div({
	margin: `${token('space.200', '16px')} 0`,
	width: '50%',
	maxWidth: '1024px',
	minWidth: '240px',
});
